import React, { useState, useEffect } from "react";
import {
  nonProtectedRoutes,
  protectedRoutes,
} from "./utils/nonProtectedRoutingPaths";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Outlet,
} from "react-router-dom";
import {
  AppShell,
  MantineProvider,
  createTheme,
  MantineColorsTuple,
  useMantineColorScheme,
} from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { checkAuth } from "./api";

// mantine
import "@mantine/core/styles.css";
import "@mantine/tiptap/styles.css";

import "./App.css";

// custom components
import Login from "./components/Login";
import PricingPage from "./pages/Pricing";
import CheckoutPage from "./pages/Checkout";
import TermsOfServicePage from "./pages/TermsOfService";
import PaymentConfirmationPage from "./pages/PaymentConfirmationPage";
import SafeGenerator from "./components/SafeGenerator";
import { Header } from "./components/Header/Header";
import PlaceholderPage from "./components/PlaceholderPage";
import EmailDemo from "./components/EmailDemo";
import SplashLanding from "./components/SplashLanding";
import SuccessPage from "./components/SuccessPage";
import UnsubscribePage from "./components/UnsubscribePage";
import Dashboard from "./components/Dashboard";
import CorporateFirmsPage from "./components/landing/CorporateFirmsPage";
import EmergingCompaniesVentureCapital from "./components/landing/EmergingCompaniesVentureCapital";
import MergersAndAcquisitions from "./components/landing/MergersAndAcquisitions";
import { AccountSetup } from "./components/auth/AccountSetup";
import Security from "./components/Security";
import ResponsibleAI from "./components/ResponsibleAI";
import { ResetPassword } from "./components/auth/ResetPassword";
import RegisterPage from "./pages/Register";
import ContactPage from "./pages/Contact";
import TenantAdminDashboardPage from "./pages/TenantAdmin";
import NotFoundPage from "./pages/NotFound";
import ProfilePage from "./pages/Profile";

const myColor: MantineColorsTuple = [
  "#f0fafa",
  "#e2f1f1",
  "#bfe4e4",
  "#9ad6d5",
  "#7dcaca",
  "#6ac2c2",
  "#5fc0bf",
  "#4ea8a8",
  "#409696",
  "#0F2D2D",
];

const theme = createTheme({
  colors: {
    myColor,
  },
  primaryColor: "myColor",
  primaryShade: 9,
  // fontFamily: 'Inria Sans, sans-serif',
  // headings: {
  //   fontFamily: 'Inria Sans, sans-serif',
  // },
  components: {
    Button: {
      defaultProps: {
        color: "#0F2D2D",
      },
    },
    Title: {
      styles: (theme) => ({
        root: {
          color: theme.colors.myColor[9],
          opacity: 0.9,
        },
      }),
    },
    Text: {
      styles: (theme) => ({
        root: {
          color: theme.colors.myColor[9],
        },
      }),
    },
  },
  other: {
    backgroundColor: {
      light: "#fcfcfc",
      dark: "#1a1a1a",
    },
  },
});

function ProtectedRoutes() {
  const { colorScheme } = useMantineColorScheme();
  const [navbarOpened, setNavbarOpened] = useState(false);

  const handleNavbarToggle = () => {
    setNavbarOpened((prev) => !prev);
  };

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 300,
        breakpoint: "sm",
        collapsed: { desktop: !navbarOpened, mobile: !navbarOpened },
      }}
      padding="md"
      styles={(theme) => ({
        main: {
          backgroundColor:
            colorScheme === "dark"
              ? theme.other.backgroundColor.dark
              : theme.other.backgroundColor.light,
        },
      })}
    >
      <Header navbarOpened={navbarOpened} onNavbarToggle={handleNavbarToggle} />

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}

function AppContent() {
  console.log("Rendering AppContent");
  return (
    <Router>
      <Routes>
        <Route path="/" element={<SplashLanding />} />
        <Route path={nonProtectedRoutes.CONTACT} element={<ContactPage />} />
        <Route path={nonProtectedRoutes.PRICING} element={<PricingPage />} />
        <Route path={nonProtectedRoutes.LOGIN} element={<Login />} />
        <Route path={nonProtectedRoutes.REGISTER} element={<RegisterPage />} />
        <Route path={nonProtectedRoutes.ACTIVATE} element={<RegisterPage />} />
        <Route
          path={nonProtectedRoutes.TERMS}
          element={<TermsOfServicePage />}
        />
        <Route
          path={nonProtectedRoutes.CORPORATE_FIRMS}
          element={<CorporateFirmsPage />}
        />
        <Route
          path={nonProtectedRoutes.MERGE_ACQUISITIONS}
          element={<MergersAndAcquisitions />}
        />
        <Route
          path={nonProtectedRoutes.EC_VC}
          element={<EmergingCompaniesVentureCapital />}
        />
        <Route path="account-setup/:token" element={<AccountSetup />} />
        <Route path={nonProtectedRoutes.SECURITY} element={<Security />} />
        <Route
          path={nonProtectedRoutes.RESPONSIBLE_AI}
          element={<ResponsibleAI />}
        />
        <Route
          path="/app/*"
          element={
            <RequireAuth>
              <ProtectedRoutes />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="email" replace />} />
          <Route path="email" element={<EmailDemo />} />
          <Route path="financings/safes" element={<SafeGenerator />} />
          <Route
            path="financings/convertible-notes"
            element={<PlaceholderPage title="Convertible Notes" />}
          />
          <Route
            path="governance/board-consent"
            element={<PlaceholderPage title="Board Consent" />}
          />
          <Route
            path="governance/stockholder-consent"
            element={<PlaceholderPage title="Stockholder Consent" />}
          />
          <Route
            path="commercial/nda"
            element={
              <PlaceholderPage title="Non-disclosure / Confidentiality" />
            }
          />
          <Route
            path="commercial/warrants"
            element={<PlaceholderPage title="Warrants" />}
          />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path={protectedRoutes.CHECKOUT} element={<CheckoutPage />} />
          <Route
            path={protectedRoutes.TENANT_ADMIN}
            element={<TenantAdminDashboardPage />}
          />
          <Route path={protectedRoutes.PROFILE} element={<ProfilePage />} />
          <Route
            path={protectedRoutes.PAYMENT_SUCCESS}
            element={<PaymentConfirmationPage />}
          />
          <Route
            path={protectedRoutes.PAYMENT_CANCEL}
            element={<PaymentConfirmationPage />}
          />
        </Route>
        <Route path="/success" element={<SuccessPage />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path={nonProtectedRoutes.NOT_FOUND} element={<NotFoundPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const verifyAuth = async () => {
      const authStatus = await checkAuth();
      setIsAuthenticated(authStatus.isValid);
      setIsLoading(false);
    };
    verifyAuth();
  }, []);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <MantineProvider theme={theme} defaultColorScheme="light">
      <Notifications
        position="bottom-right"
        zIndex={2077}
        containerWidth={500}
        mt="sm"
        autoClose={false}
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          maxWidth: "500px",
          marginBottom: "20px",
          marginTop: "20px",
        }}
      />
      <AppContent />
    </MantineProvider>
  );
}

export default App;
